import { type Dispatch, type SetStateAction, useEffect, useMemo } from 'react'
import { useIsOnline } from './useIsOnline'
import { clearGlobalState } from '../utilities/createGlobalState'
import { REST_API } from '@/utilities/envVariables'
import { routes } from '@/utilities/routes'
import { type HttpRouteSpec, type SessionSummary, UserRole } from 'aevocam-rest-api-spec'
import { useApiStatus } from './useApiStatus'
import { useNavigate } from 'react-router-dom'
import { useTenantId } from './useTenantId'
import { rememberLoginRedirectTarget } from '@/utilities/authRedirect'

//--------------------------------------------------------------------------------
export type UseApiHook = {
  request: <
    Path extends Record<string, string> | undefined,
    Query extends Record<string, string> | undefined,
    Body extends object | undefined,
    Return extends object | undefined
  >(
    route: HttpRouteSpec<Path, Query, Body, Return>,
    props?: Omit<ApiJsonFetchProps<Return>, 'url' | 'method' | 'allowAnonymous'> & {
      path?: Path,
      query?: Query,
      body?: Body,
    }
  ) => Promise<ApiResponse<Return>>,
  /**
   * GET a binary response (e.g. PDF). Uses the same path/query resolution as `request`
   * but returns the response body as a Blob instead of parsing JSON.
   */
  requestBlob: <
    Path extends Record<string, string> | undefined,
  >(
    route: HttpRouteSpec<Path, undefined, undefined, Blob>,
    props?: {
      path?: Path
    }
  ) => Promise<ApiResponse<Blob>>,
  get: <T>(props: ApiJsonFetchProps<T>) => Promise<ApiResponse<T>>
  post: <T>(props: ApiJsonFetchWithBodyProps<T>) => Promise<ApiResponse<T>>
  put: <T>(props: ApiJsonFetchWithBodyProps<T>) => Promise<ApiResponse<T>>
  patch: <T>(props: ApiJsonFetchWithBodyProps<T>) => Promise<ApiResponse<T>>
  delete: <T>(props: ApiJsonFetchProps<T>) => Promise<ApiResponse<T>>
  status: ApiStatus
  setStatus: Dispatch<SetStateAction<ApiStatus>>
  redirectToLogin: () => void
}

type UseApiProps = {
  requireLogin?: boolean
  adminOnly?: boolean
}

//--------------------------------------------------------------------------------
/** Hook to make REST API calls */
export function useApi({
  requireLogin = false,
  adminOnly = false,
}: UseApiProps = {}) {

  const tenantId = useTenantId()
  const navigate = useNavigate()
  const isOnline = useIsOnline()

  const [apiStatus, setApiStatus] = useApiStatus()

  // When the user logs out, clear global state for security
  useEffect(() => {
    if (!apiStatus.session?.loggedIn) {
      clearGlobalState()  // Don't leave anything in memory for hackers to exploit
    }
  }, [apiStatus.session?.loggedIn])

  //--------------------------------------------------------------------------------
  async function request<
    Path extends Record<string, string> | undefined,
    Query extends Record<string, string> | undefined,
    Body extends object | undefined,
    Return extends object | undefined
  >(
    route: HttpRouteSpec<Path, Query, Body, Return>,
    props: {
      path?: Path,
      query?: Query,
      body?: Body,
    } = {}
  ) {
    const res = await apiJsonFetch({
      url: route.uri,
      method: route.method,
      allowAnonymous: route.allowAnonymous,
      ...props,
      query: { ...props.path, ...props.query },
      apiStatus,
      isOnline,
      redirectToLogin,
    }) as ApiResponse<Return>
    return res
  }

  //--------------------------------------------------------------------------------
  async function requestBlob<
    Path extends Record<string, string> | undefined,
  >(
    route: HttpRouteSpec<Path, undefined, undefined, Blob>,
    props: {
      path?: Path
    } = {}
  ) {
    return apiBlobFetch({
      url: route.uri,
      method: route.method,
      allowAnonymous: route.allowAnonymous,
      query: props.path as Record<string, string | undefined>,
      apiStatus,
      isOnline,
      redirectToLogin,
    })
  }

  //--------------------------------------------------------------------------------
  const api: UseApiHook = useMemo(() => {
    return {
      request,
      requestBlob,
      get: <T>(props: ApiJsonFetchProps<T>) => apiJsonFetch({ ...props, method: 'GET', apiStatus, isOnline, redirectToLogin }),
      post: <T>(props: ApiJsonFetchWithBodyProps<T>) => apiJsonFetch({ ...props, method: 'POST', apiStatus, isOnline, redirectToLogin }),
      put: <T>(props: ApiJsonFetchWithBodyProps<T>) => apiJsonFetch({ ...props, method: 'PUT', apiStatus, isOnline, redirectToLogin }),
      patch: <T>(props: ApiJsonFetchWithBodyProps<T>) => apiJsonFetch({ ...props, method: 'PATCH', apiStatus, isOnline, redirectToLogin }),
      delete: <T>(props: ApiJsonFetchProps<T>) => apiJsonFetch({ ...props, method: 'DELETE', apiStatus, isOnline, redirectToLogin }),
      status: apiStatus,
      setStatus: setApiStatus,
      redirectToLogin,
    }
  }, [apiStatus])

  //--------------------------------------------------------------------------------
  function redirectToLogin() {
    const redirectTo = window.location.pathname + window.location.search + window.location.hash
    console.log('redirecting to login from ', redirectTo)
    rememberLoginRedirectTarget(redirectTo)
    window.location.href = routes.logIn(tenantId)
  }

  //--------------------------------------------------------------------------------
  if (requireLogin && apiStatus.initialized && !apiStatus.session?.loggedIn) {
    redirectToLogin()
  }

  //--------------------------------------------------------------------------------
  if (
    requireLogin &&
    apiStatus.initialized &&
    apiStatus.session?.tenantId &&
    !apiStatus.session.isTenantMember &&
    !apiStatus.session.systemAdmin
  ) {
    redirectToLogin()
  }

  //--------------------------------------------------------------------------------
  if (adminOnly && apiStatus.initialized && apiStatus.session?.loggedIn) {
    if (apiStatus.session?.userRole !== UserRole.Admin)
      navigate(routes.home())
  }

  //--------------------------------------------------------------------------------
  return api
}


//--------------------------------------------------------------------------------
type QueryRecord = Record<string, undefined | null | string | number | boolean | Date> | undefined

//--------------------------------------------------------------------------------
/** Resolve path placeholders and append query string — shared by JSON and blob fetchers */
function resolveApiUrlPath(uriTemplate: string, query?: QueryRecord): string {
  let url = uriTemplate
  let newQuery: Record<string, string> | undefined = undefined

  // Convert the query-string values to strings
  if (query) {
    newQuery = {}
    Object.entries(query)
      .filter(([_, value]) => value !== undefined)  // Filter out keys with undefined values
      .forEach(([key, value]) => {
        let str: string
        if (value === null)
          str = ''
        else if (value instanceof Date)
          str = value.toISOString()
        else
          str = value!.toString()

        // It's possible this query string key is actually meant for the URL path
        const match = url.match(`{${key}}|\\[${key}]`)
        if (match) {
          url = url.replace(match[0], str)
          return  // Remove this key from the query string because it's meant for the URL path
        }

        // Pack it into the new, trimmed-down query string
        newQuery![key] = str
      })
  }

  const apiUrl = `${url}${Object.keys(newQuery ?? {}).length > 0
    ? '?' + new URLSearchParams(newQuery).toString()
    : ''
  }`
  return apiUrl
}

//--------------------------------------------------------------------------------
/** GET binary content (e.g. PDF) */
async function apiBlobFetch({
  url: uriTemplate,
  method,
  query,
  allowAnonymous,
  apiStatus,
  isOnline,
  redirectToLogin,
}: {
  url: string
  method: string
  query?: QueryRecord
  allowAnonymous?: boolean
  apiStatus: ApiStatus
  isOnline: boolean
  redirectToLogin: () => void
}): Promise<ApiResponse<Blob>> {
  if (!isOnline) {
    return { status: 503, error: 'Network disconnected' }
  }

  if (apiStatus.initialized && !apiStatus.session?.loggedIn && !allowAnonymous) {
    redirectToLogin()
    return { status: 401, error: 'Unauthorized' }
  }

  const apiUrl = resolveApiUrlPath(uriTemplate, query)
  const fullApiUrl = REST_API + apiUrl
  const sessionId = localStorage.getItem('sessionId')

  const response = await fetch(fullApiUrl, {
    method,
    headers: {
      Accept: 'application/pdf',
      ...(sessionId ? { 'Authorization': `Bearer ${sessionId}` } : {}),
    },
    cache: 'no-cache',
  })

  if (response.ok) {
    const blob = await response.blob()
    return { status: response.status, data: blob }
  }

  const ct = response.headers.get('content-type') ?? ''
  if (ct.includes('application/json')) {
    try {
      const json = (await response.json()) as ApiResponse<unknown>
      return {
        status: response.status,
        error: json.error ?? response.statusText,
      }
    } catch {
      return { status: response.status, error: response.statusText }
    }
  }

  try {
    const text = await response.text()
    return {
      status: response.status,
      error: text?.slice(0, 200) || response.statusText,
    }
  } catch {
    return { status: response.status, error: response.statusText }
  }
}

//--------------------------------------------------------------------------------
/** Wrapper for REST API calls */
async function apiJsonFetch<T>({
  method,
  url: uriTemplate,
  query,
  body,
  allowAnonymous,
  returnErrors,
  apiStatus,
  isOnline,
  redirectToLogin,
}: ApiJsonFetchAllProps<T>): Promise<ApiResponse<T>> {
  const apiUrl = resolveApiUrlPath(uriTemplate, query)

  // The caller can optionally pass in a mock response to simulate a response from the API
  if (!isOnline) {
    return { status: 503, error: 'Network disconnected' }
  }

  // If the user isn't logged in then redirect to the login page. Unless
  // the caller declares that this call is allowed to be anonymous.
  if (apiStatus.initialized && !apiStatus.session?.loggedIn && !allowAnonymous) {
    redirectToLogin()
    return { status: 401, error: 'Unauthorized' }
  }

  const fullApiUrl = REST_API + apiUrl

  const sessionId = localStorage.getItem('sessionId')
  const response = await fetch(fullApiUrl, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(sessionId ? { Authorization: `Bearer ${sessionId}` } : {}),
    },
    cache: 'no-cache',
    body: body ? JSON.stringify(
      body,
      (key, value) => {
        if (typeof value === 'bigint') return value.toString()
        return value
      },
      2
    ) : undefined,
  })
  const json = await response.json() as ApiResponse<T>
  if (json.error && returnErrors) {
    throw new Error(json.status + ': ' + json.error)
  }
  return json
}


//--------------------------------------------------------------------------------

export type ApiResponse<T> = {
  status: number
  data?: T
  error?: string | null
  logOut?: boolean
}

export type ApiStatus = {
  initialized: boolean
  needsRefresh: boolean
  session?: SessionSummary
}

type ApiJsonFetchProps<T> = {
  url: string
  query?: Record<string, (undefined | null | string | number | boolean | Date)>
  allowAnonymous?: boolean
  returnErrors?: boolean
  mockResponse?: Partial<ApiResponse<T>>
}

type ApiJsonFetchWithBodyProps<T> = ApiJsonFetchProps<T> & {
  body?: unknown
}

type ApiJsonFetchAllProps<T> = ApiJsonFetchWithBodyProps<T> & {
  method: string
  apiStatus: ApiStatus
  isOnline: boolean
  redirectToLogin: () => void
}
